import React, { useState } from "react";
import Select, { MultiValue } from "react-select";
import { slaData } from "../../utils/Sla/SlaData";
import "./SlaTable.css";

interface SlaGroup {
  customer: string[];
  mappings: {
    [key: string]: string;
  };
}

interface Option {
  value: string;
  label: string;
}

const impacts = ["Critical", "High", "Medium", "Low"];
const urgency = "Critical"; // only first urgency

const SlaTable: React.FC = () => {
  const allCustomers: string[] = Array.from(
    new Set(slaData.flatMap((group: SlaGroup) => group.customer))
  );

  // ✅ Add ALL option
  const options: Option[] = [
    { value: "ALL", label: "All" },
    ...allCustomers.map((c) => ({ value: c, label: c })),
  ];

  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);

  // ✅ Handle ALL selection
  const handleSelectChange = (selected: MultiValue<Option>) => {
    if (!selected) {
      setSelectedCustomers([]);
      return;
    }

    // ✅ When "All" is selected → return ALL customers
    if (selected.some((o) => o.value === "ALL")) {
      setSelectedCustomers(allCustomers);
      return;
    }

    setSelectedCustomers(selected.map((o) => o.value));
  };

  const findSla = (customer: string, impact: string): string => {
    const group = slaData.find((g) => g.customer.includes(customer));
    if (!group) return "-";
    const key = `${impact}|${urgency}`;
    return (group.mappings as Record<string, string>)[key] || "-";
  };

  return (
    <div className="sla-container">
      <h1>SLA Configuration</h1>

      <div className="dropdown-container">
        <label>Select Customer(s): </label>
        <Select
          options={options}
          isMulti
          onChange={handleSelectChange}
          placeholder="Select customers..."
        />
      </div>

      {selectedCustomers.length > 0 ? (
        <table className="sla-table">
          <thead>
            <tr>
              <th>Customer</th>
              {impacts.map((impact) => (
                <th key={impact}>{impact}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {selectedCustomers.map((customer) => (
              <tr key={customer}>
                <td>
                  <strong>{customer}</strong>
                </td>
                {impacts.map((impact) => (
                  <td key={`${customer}-${impact}`}>
                    {findSla(customer, impact)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="info-text">
          Please select one or more customers to view their SLA mappings.
        </p>
      )}
    </div>
  );
};

export default SlaTable;
